'use client';

import { useState, useEffect, useRef } from 'react';
import { BoardType, PositionType, PlayerType } from '../types';

type BoardProps = {
  board: BoardType;
  currentPlayer: PlayerType;
  gameOver: boolean;
  lastMove: PositionType | null;
  onPlaceStone: (position: PositionType) => void;
  isAITurn: boolean;
};

export const Board: React.FC<BoardProps> = ({
  board,
  currentPlayer,
  gameOver,
  lastMove,
  onPlaceStone,
  isAITurn,
}) => {
  const [hoverPos, setHoverPos] = useState<PositionType | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const boardSize = 15;
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 600 });

  // Draw the board
  const drawBoard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const { width, height } = canvas;
    const cellSize = width / (boardSize + 1);
    const startPos = cellSize;
    const endPos = width - cellSize;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw board background
    ctx.fillStyle = '#f8e3c5'; // Light wood color
    ctx.fillRect(0, 0, width, height);
    
    // Draw grid lines
    ctx.beginPath();
    ctx.strokeStyle = '#8d6e63'; // Wood brown color
    ctx.lineWidth = 1;
    
    // Draw horizontal lines
    for (let i = 0; i < boardSize; i++) {
      const y = startPos + i * cellSize;
      ctx.moveTo(startPos, y);
      ctx.lineTo(endPos, y);
    }
    
    // Draw vertical lines
    for (let i = 0; i < boardSize; i++) {
      const x = startPos + i * cellSize;
      ctx.moveTo(x, startPos);
      ctx.lineTo(x, endPos);
    }
    
    ctx.stroke();
    
    // Draw the star points (standard points in Gomoku)
    const starPoints = [3, 7, 11];
    ctx.fillStyle = '#3e2723';
    
    starPoints.forEach(row => {
      starPoints.forEach(col => {
        const x = startPos + col * cellSize;
        const y = startPos + row * cellSize;
        
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.fill();
      });
    });
    
    // Draw stones
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        const stone = board[row][col];
        if (stone) {
          const x = startPos + col * cellSize;
          const y = startPos + row * cellSize;
          const radius = cellSize * 0.45;
          
          // Draw stone shadow
          ctx.beginPath();
          ctx.arc(x + 2, y + 2, radius, 0, 2 * Math.PI);
          ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
          ctx.fill();
          
          // Draw stone
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, 2 * Math.PI);
          
          // Create gradient for a 3D effect
          const gradient = ctx.createRadialGradient(
            x - radius/3, y - radius/3, radius/10,
            x, y, radius
          );
          
          if (stone === 'BLACK') {
            gradient.addColorStop(0, '#5D5D5D');
            gradient.addColorStop(1, '#1A1A1A');
          } else {
            gradient.addColorStop(0, '#FFFFFF');
            gradient.addColorStop(1, '#D0D0D0');
          }
          
          ctx.fillStyle = gradient;
          ctx.fill();
          
          // Highlight the last move
          if (lastMove && lastMove.row === row && lastMove.col === col) {
            ctx.beginPath();
            ctx.arc(x, y, radius + 2, 0, 2 * Math.PI);
            ctx.strokeStyle = '#3498db';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        }
      }
    }
    
    // Draw hover indicator (only when it's not AI's turn and game is not over)
    if (!gameOver && !isAITurn && hoverPos) {
      const { row, col } = hoverPos;
      if (board[row][col] === null) {
        const x = startPos + col * cellSize;
        const y = startPos + row * cellSize;
        const radius = cellSize * 0.45;
        
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = currentPlayer === 'BLACK' 
          ? 'rgba(0, 0, 0, 0.3)' 
          : 'rgba(255, 255, 255, 0.5)';
        ctx.fill();
      }
    }
  };

  // Convert canvas coordinates to board position
  const getBoardPosition = (x: number, y: number): PositionType | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    const rect = canvas.getBoundingClientRect();
    const cellSize = canvas.width / (boardSize + 1);
    const startPos = cellSize;
    
    // Convert client coordinates to canvas coordinates
    const canvasX = (x - rect.left) * (canvas.width / rect.width);
    const canvasY = (y - rect.top) * (canvas.height / rect.height);
    
    // Check if within the board bounds
    if (canvasX < startPos - cellSize/2 || canvasX > canvas.width - startPos + cellSize/2 || 
        canvasY < startPos - cellSize/2 || canvasY > canvas.height - startPos + cellSize/2) {
      return null;
    }
    
    // Convert to board coordinates
    const col = Math.round((canvasX - startPos) / cellSize);
    const row = Math.round((canvasY - startPos) / cellSize);
    
    // Check if valid board coordinates
    if (row >= 0 && row < boardSize && col >= 0 && col < boardSize) {
      return { row, col };
    }
    
    return null;
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isAITurn) return; // Don't show hover effect during AI's turn
    
    const pos = getBoardPosition(e.clientX, e.clientY);
    setHoverPos(pos);
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setHoverPos(null);
  };

  // Handle click
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (gameOver || isAITurn) return;
    
    const pos = getBoardPosition(e.clientX, e.clientY);
    if (pos && board[pos.row][pos.col] === null) {
      onPlaceStone(pos);
    }
  };

  // Handle touch events for mobile
  const handleTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (gameOver || isAITurn) return;
    
    e.preventDefault();
    
    const touch = e.touches[0];
    const pos = getBoardPosition(touch.clientX, touch.clientY);
    if (pos && board[pos.row][pos.col] === null) {
      onPlaceStone(pos);
    }
  };

  // Resize handler for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setCanvasSize({ 
          width: Math.min(600, width),
          height: Math.min(600, width)
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Draw or redraw the board when necessary
  useEffect(() => {
    drawBoard();
  }, [board, lastMove, hoverPos, gameOver, isAITurn, canvasSize]);

  // Determine cursor style
  const getCursorStyle = () => {
    if (gameOver || isAITurn) {
      return 'cursor-not-allowed';
    }
    return 'cursor-pointer';
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full max-w-[600px] mx-auto aspect-square rounded-lg shadow-md"
    >
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className={`w-full h-full rounded-lg ${getCursorStyle()}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchStart={handleTouch}
      />
      
      {isAITurn && (
        <div className="absolute top-2 left-2 bg-white/80 rounded-md py-1 px-2 text-sm font-medium text-gray-600 flex items-center">
          <svg className="animate-spin mr-1.5 h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          AI is making a move...
        </div>
      )}
    </div>
  );
};

export default Board;